import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toastUtils } from '@/hooks/useToast';
import apiCoreAdapter from '@/hooks/apiCoreAdapter';
import { activityTracker } from '@/utils/activityTracker';
import {
  ALREADY_COMMUNICATED_OPTIONS,
  ChoiceOption,
  DERIVATION_REASON_OPTIONS,
  DerivationFormValues,
  EVOLUTION_OPTIONS,
  FAMILY_SITUATION_OPTIONS,
  HAS_PSYCHIATRIST_OPTIONS,
  LIFESTYLE_OPTIONS,
  MAIN_CONSULTATION_TOPICS,
  MEDICAL_HISTORY_OPTIONS,
  RISK_FIELDS,
  SUPPORT_NETWORK_OPTIONS,
  WHEN_COMMUNICATED_OPTIONS,
  WORK_SITUATION_OPTIONS,
  emptyDerivationFormValues,
  isMostSevere,
} from './derivationFormSchema';

interface DerivationWizardProps {
  processId: string;
  patientEmail: string;
  patientName: string;
  psychologistEmail: string;
  sessionsCountGrade: string;
  onSubmitted: () => void;
}

type Field = keyof DerivationFormValues;

const TOTAL_STEPS = 4;

function RequiredMark() {
  return <span className="text-red-500 ml-0.5">*</span>;
}

function FieldBlock({
  label,
  htmlFor,
  required,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-foreground">
        {label}
        {required && <RequiredMark />}
      </label>
      {children}
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}

function ChoiceGroup({
  options,
  value,
  onChange,
}: {
  options: ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div role="radiogroup" className="flex flex-col gap-2">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex items-start gap-3 rounded-md border px-3 py-2.5 text-left text-sm transition-colors',
              selected
                ? 'border-primary bg-primary/5 text-foreground'
                : 'border-border bg-background text-foreground hover:bg-muted'
            )}
          >
            <span
              className={cn(
                'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                selected ? 'border-primary' : 'border-muted-foreground'
              )}
            >
              {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
            </span>
            <span>{opt.value}</span>
          </button>
        );
      })}
    </div>
  );
}

function DropdownField({
  options,
  value,
  onChange,
  ariaLabel,
  placeholder = 'Seleccioná una opción',
}: {
  options: ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
}) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.value}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function TextInput({
  id,
  value,
  onChange,
  placeholder,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      id={id}
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
    />
  );
}

export const DerivationWizard: React.FC<DerivationWizardProps> = ({
  processId,
  patientEmail,
  patientName,
  psychologistEmail,
  sessionsCountGrade,
  onSubmitted,
}) => {
  const [step, setStep] = useState(1);
  const [values, setValues] = useState<DerivationFormValues>(() => ({
    ...emptyDerivationFormValues(),
    process_id: processId,
    patient_email: patientEmail,
    // `patient_name` no forma parte de la interfaz que espera Make (resuelve el nombre
    // server-side vía getPatient con el email) pero SÍ hace falta acá: es lo que persiste
    // DerivationSubmissionsService para poder buscar por paciente en el panel admin.
    patient_name: patientName,
    psychologist_email: psychologistEmail,
    sessions_count_grade: sessionsCountGrade,
  }));
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends Field>(key: K, value: DerivationFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const workSituationSubfield = useMemo(() => {
    switch (values.work_situation) {
      case 'Empleado a tiempo completo':
      case 'Empleado a tiempo parcial':
      case 'Autónomo':
        return { key: 'occupation' as const, label: 'Ocupación' };
      case 'Estudiante':
        return { key: 'career_studying' as const, label: 'Carrera/Terciario que está estudiando' };
      case 'Otro':
        return { key: 'work_situation_other_detail' as const, label: 'Especificá tu situación laboral' };
      default:
        return null;
    }
  }, [values.work_situation]);

  const validateStep2 = (v: DerivationFormValues) => {
    const e: Partial<Record<Field, string>> = {};
    if (!v.already_communicated_to_patient) e.already_communicated_to_patient = 'Requerido';
    if (v.already_communicated_to_patient === 'Si' && !v.when_communicated_to_patient) {
      e.when_communicated_to_patient = 'Requerido';
    }
    if (!v.derivation_reason) e.derivation_reason = 'Requerido';
    if (!v.main_consultation_topic) e.main_consultation_topic = 'Requerido';
    return e;
  };

  const validateStep3 = (v: DerivationFormValues) => {
    const e: Partial<Record<Field, string>> = {};
    if (!v.emergency_contact_relationship_has.trim()) e.emergency_contact_relationship_has = 'Requerido';
    if (!v.emergency_contact_relationship.trim()) e.emergency_contact_relationship = 'Requerido';
    if (!v.has_psychiatrist_contact) e.has_psychiatrist_contact = 'Requerido';
    if (v.has_psychiatrist_contact === 'Tiene' && !v.psychiatrist_phone.trim()) {
      e.psychiatrist_phone = 'Requerido';
    }
    if (!v.family_situation) e.family_situation = 'Requerido';
    if (!v.work_situation) e.work_situation = 'Requerido';
    if (workSituationSubfield && !v[workSituationSubfield.key].trim()) {
      e[workSituationSubfield.key] = 'Requerido';
    }
    if (!v.support_network) e.support_network = 'Requerido';
    if (!v.relevant_medical_history) e.relevant_medical_history = 'Requerido';
    if (!v.lifestyle_habits) e.lifestyle_habits = 'Requerido';
    if (!v.main_problems_addressed.trim()) e.main_problems_addressed = 'Requerido';
    if (!v.patient_evolution) e.patient_evolution = 'Requerido';
    if (!v.recommendations_for_new_therapist.trim()) e.recommendations_for_new_therapist = 'Requerido';
    return e;
  };

  const validateStep4 = (v: DerivationFormValues) => {
    const e: Partial<Record<Field, string>> = {};
    for (const field of RISK_FIELDS) {
      const levelValue = v[field.key as Field] as string;
      if (!levelValue) e[field.key as Field] = 'Requerido';
      if (isMostSevere(field, levelValue) && !(v[field.detailKey as Field] as string).trim()) {
        e[field.detailKey as Field] = 'Requerido';
      }
    }
    return e;
  };

  const goNext = () => {
    let stepErrors: Partial<Record<Field, string>> = {};
    if (step === 2) stepErrors = validateStep2(values);
    if (step === 3) stepErrors = validateStep3(values);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      toastUtils.error('Completá los campos requeridos antes de continuar.');
      return;
    }
    setErrors({});
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    const stepErrors = validateStep4(values);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      toastUtils.error('Completá los campos requeridos antes de enviar.');
      return;
    }
    setSubmitting(true);
    try {
      // `values` trae también `psychologist_email` (no lo pide el backend — lo saca de
      // @AuthPsi(), nunca del body — ver DerivationSubmissionsService.submit); el campo de
      // más no molesta, el schema zod del backend descarta claves desconocidas en silencio.
      await apiCoreAdapter.submitDerivation(values);
      toastUtils.success('Solicitud de derivación enviada correctamente');
      // Métricas de Actividad (funnel derivacion): se envió con éxito.
      activityTracker.trackAction('derivation_submitted');
      onSubmitted();
    } catch {
      // beHttp ya mostró el toast de error (mensaje del backend, incluido un {error} de
      // Make relayado como 409 — ver DerivationSubmissionsService.submit).
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Paso {step} de {TOTAL_STEPS}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6 shadow-sm md:p-8">
        {step === 1 && (
          <div className="flex flex-col gap-5">
            <h3 className="text-lg font-bold text-foreground">Identificación</h3>
            <FieldBlock label="Email Psicólogo">
              <div className="rounded-md bg-muted px-3 py-2 text-sm text-foreground">{psychologistEmail}</div>
            </FieldBlock>
            <FieldBlock label="Email Paciente">
              <div className="rounded-md bg-muted px-3 py-2 text-sm text-foreground">{patientEmail}</div>
            </FieldBlock>
            <FieldBlock label="Nombre Paciente">
              <div className="rounded-md bg-muted px-3 py-2 text-sm text-foreground">{patientName || '—'}</div>
            </FieldBlock>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-bold text-foreground">Contexto de la derivación</h3>
            <FieldBlock label="¿Ya se lo comunicaste al paciente?" required error={errors.already_communicated_to_patient}>
              <ChoiceGroup
                options={ALREADY_COMMUNICATED_OPTIONS}
                value={values.already_communicated_to_patient}
                onChange={(v) => set('already_communicated_to_patient', v)}
              />
            </FieldBlock>
            {values.already_communicated_to_patient === 'Si' && (
              <FieldBlock label="¿Cuándo le comentaste al paciente?" required error={errors.when_communicated_to_patient}>
                <DropdownField
                  ariaLabel="¿Cuándo le comentaste al paciente?"
                  options={WHEN_COMMUNICATED_OPTIONS}
                  value={values.when_communicated_to_patient}
                  onChange={(v) => set('when_communicated_to_patient', v)}
                />
              </FieldBlock>
            )}
            <FieldBlock label="¿Qué razón te lleva a querer derivar?" required error={errors.derivation_reason}>
              <ChoiceGroup
                options={DERIVATION_REASON_OPTIONS}
                value={values.derivation_reason}
                onChange={(v) => set('derivation_reason', v)}
              />
            </FieldBlock>
            <FieldBlock
              label="Selecciona el motivo de consulta actual principal, que has relevado al conocer al paciente"
              required
              error={errors.main_consultation_topic}
            >
              <DropdownField
                ariaLabel="Motivo de consulta actual principal"
                options={MAIN_CONSULTATION_TOPICS}
                value={values.main_consultation_topic}
                onChange={(v) => set('main_consultation_topic', v)}
              />
            </FieldBlock>
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col gap-6">
            <h3 className="text-lg font-bold text-foreground">Datos del paciente</h3>
            <FieldBlock
              label="Teléfono de contacto de emergencia"
              required
              error={errors.emergency_contact_relationship_has}
            >
              <TextInput
                value={values.emergency_contact_relationship_has}
                onChange={(v) => set('emergency_contact_relationship_has', v)}
                placeholder="Ej: +54 9 11 1234-5678"
              />
            </FieldBlock>
            <FieldBlock
              label="Vínculo del contacto de emergencia con el paciente"
              required
              error={errors.emergency_contact_relationship}
            >
              <TextInput
                value={values.emergency_contact_relationship}
                onChange={(v) => set('emergency_contact_relationship', v)}
                placeholder="Ej: Padre, Hija, Hermana, Novio"
              />
            </FieldBlock>
            <FieldBlock label="Contacto del psiquiatra (si lo tuviera)" required error={errors.has_psychiatrist_contact}>
              <ChoiceGroup
                options={HAS_PSYCHIATRIST_OPTIONS}
                value={values.has_psychiatrist_contact}
                onChange={(v) => set('has_psychiatrist_contact', v)}
              />
            </FieldBlock>
            {values.has_psychiatrist_contact === 'Tiene' && (
              <FieldBlock label="Teléfono del psiquiatra" required error={errors.psychiatrist_phone}>
                <TextInput
                  value={values.psychiatrist_phone}
                  onChange={(v) => set('psychiatrist_phone', v)}
                  placeholder="Ej: +54 9 11 1234-5678"
                />
              </FieldBlock>
            )}
            <FieldBlock label="Situación familiar" required error={errors.family_situation}>
              <DropdownField
                ariaLabel="Situación familiar"
                options={FAMILY_SITUATION_OPTIONS}
                value={values.family_situation}
                onChange={(v) => set('family_situation', v)}
              />
            </FieldBlock>
            <FieldBlock label="Situación laboral" required error={errors.work_situation}>
              <DropdownField
                ariaLabel="Situación laboral"
                options={WORK_SITUATION_OPTIONS}
                value={values.work_situation}
                onChange={(v) => set('work_situation', v)}
              />
            </FieldBlock>
            {workSituationSubfield && (
              <FieldBlock label={workSituationSubfield.label} required error={errors[workSituationSubfield.key]}>
                <TextInput
                  value={values[workSituationSubfield.key]}
                  onChange={(v) => set(workSituationSubfield.key, v)}
                />
              </FieldBlock>
            )}
            <FieldBlock label="Red de Apoyo" required error={errors.support_network}>
              <DropdownField
                ariaLabel="Red de Apoyo"
                options={SUPPORT_NETWORK_OPTIONS}
                value={values.support_network}
                onChange={(v) => set('support_network', v)}
              />
            </FieldBlock>
            <FieldBlock label="Antecedentes médicos relevantes" required error={errors.relevant_medical_history}>
              <DropdownField
                ariaLabel="Antecedentes médicos relevantes"
                options={MEDICAL_HISTORY_OPTIONS}
                value={values.relevant_medical_history}
                onChange={(v) => set('relevant_medical_history', v)}
              />
            </FieldBlock>
            <FieldBlock label="Hábitos de vida" required error={errors.lifestyle_habits}>
              <DropdownField
                ariaLabel="Hábitos de vida"
                options={LIFESTYLE_OPTIONS}
                value={values.lifestyle_habits}
                onChange={(v) => set('lifestyle_habits', v)}
              />
            </FieldBlock>
            <FieldBlock
              label="Principales problemas abordados durante el tratamiento"
              htmlFor="main_problems_addressed"
              required
              error={errors.main_problems_addressed}
            >
              <Textarea
                id="main_problems_addressed"
                value={values.main_problems_addressed}
                onChange={(e) => set('main_problems_addressed', e.target.value)}
                rows={4}
              />
            </FieldBlock>
            <FieldBlock label="Evolución del paciente" required error={errors.patient_evolution}>
              <DropdownField
                ariaLabel="Evolución del paciente"
                options={EVOLUTION_OPTIONS}
                value={values.patient_evolution}
                onChange={(v) => set('patient_evolution', v)}
              />
            </FieldBlock>
            <FieldBlock
              label="Recomendaciones para el nuevo terapeuta"
              htmlFor="recommendations_for_new_therapist"
              required
              error={errors.recommendations_for_new_therapist}
            >
              <Textarea
                id="recommendations_for_new_therapist"
                value={values.recommendations_for_new_therapist}
                onChange={(e) => set('recommendations_for_new_therapist', e.target.value)}
                rows={4}
              />
            </FieldBlock>
          </div>
        )}

        {step === 4 && (
          <div className="flex flex-col gap-8">
            <div>
              <h3 className="text-lg font-bold text-foreground">Screening de riesgo</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Respondé cada punto en base a lo relevado durante las sesiones con el paciente.
              </p>
            </div>
            {RISK_FIELDS.map((field) => {
              const levelValue = values[field.key as Field] as string;
              const mostSevere = isMostSevere(field, levelValue);
              return (
                <div key={field.key} className="flex flex-col gap-3 border-t border-border pt-6 first:border-t-0 first:pt-0">
                  <div>
                    <h4 className="text-sm font-bold text-foreground">{field.title}</h4>
                    <p className="mt-0.5 text-sm text-muted-foreground">{field.question}</p>
                  </div>
                  <FieldBlock label="" error={errors[field.key as Field]}>
                    <ChoiceGroup
                      options={field.levels}
                      value={levelValue}
                      onChange={(v) => set(field.key as Field, v)}
                    />
                  </FieldBlock>
                  {mostSevere && (
                    <FieldBlock
                      label="Contanos más en detalle"
                      htmlFor={field.detailKey}
                      required
                      error={errors[field.detailKey as Field]}
                    >
                      <Textarea
                        id={field.detailKey}
                        value={values[field.detailKey as Field] as string}
                        onChange={(e) => set(field.detailKey as Field, e.target.value)}
                        rows={3}
                      />
                    </FieldBlock>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between border-t border-border pt-6">
          <Button type="button" variant="outline" onClick={goBack} disabled={step === 1 || submitting}>
            Atrás
          </Button>
          {step < TOTAL_STEPS ? (
            <Button type="button" onClick={goNext}>
              Siguiente
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Enviando…' : 'Enviar solicitud'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DerivationWizard;
